import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Typography, Chip, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Pagination, TextField, Button, Tooltip, Dialog, DialogContent, IconButton, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import Swal from "sweetalert2";
import Header from "components/Header";
import AdjustBalanceDialog from "components/AdjustBalanceDialog";
import { getSlipBotEvents, slipBotMediaUrl, createManualTopup, reviewSlipBotEvent } from "api/slipBot";
import { getTopupRequestDetails, rejectTopupRequest, getCustomerAccountByAdmin } from "api/wallet";

// Read-only audit view of every image the WhatsApp slip bot inspected
// (SlipBotEvent) — evidence photo, bank-slip classifier verdict, OCR
// candidates, and what the matcher decided. The production counterpart of
// the dev-only /slipbot-test page.

const OUTCOMES = [
  "created", "created_flagged", "auto_approved", "no_match", "no_outstanding",
  "ocr_failed", "duplicate", "error", "not_a_slip",
  "dry_run_match", "dry_run_near_match", "dry_run_no_match", "dry_run_not_a_slip",
];

const OUTCOME_COLOR = {
  created: "success",
  created_flagged: "warning",
  auto_approved: "success",
  no_match: "default",
  no_outstanding: "default",
  ocr_failed: "error",
  duplicate: "warning",
  error: "error",
  not_a_slip: "warning",
  dry_run_match: "info",
  dry_run_near_match: "info",
  dry_run_no_match: "info",
  dry_run_not_a_slip: "info",
};

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

function ClassifierChip({ ocr, t }) {
  if (!ocr || ocr.slipScore == null) {
    return <Chip size="small" variant="outlined" label={t("slipbotHistory.noClassifier")} />;
  }
  return (
    <Chip
      size="small"
      color={ocr.isBankSlip ? "success" : "default"}
      variant={ocr.isBankSlip ? "filled" : "outlined"}
      label={`${ocr.isBankSlip ? t("slipbotHistory.bankSlip") : t("slipbotHistory.notASlip")} (${ocr.slipScore})`}
    />
  );
}

function Thumb({ event, size = 56, onClick }) {
  const filename = event.mediaFile && event.mediaFile.filename;
  if (!filename) {
    return (
      <Box sx={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "action.hover", borderRadius: 1 }}>
        <ImageNotSupportedIcon fontSize="small" color="disabled" />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={slipBotMediaUrl(filename)}
      alt={filename}
      loading="lazy"
      onClick={onClick}
      sx={{ width: size, height: size, objectFit: "cover", borderRadius: 1, cursor: "zoom-in", border: "1px solid", borderColor: "divider" }}
    />
  );
}

// Mirrors backend/controllers/slipBot.js CREATABLE_OUTCOMES — "duplicate" is
// excluded there (and here) to avoid double-crediting an already-processed slip.
const CREATABLE_OUTCOMES = [
  "not_a_slip", "no_match", "no_outstanding", "ocr_failed", "error",
  "dry_run_match", "dry_run_near_match", "dry_run_no_match", "dry_run_not_a_slip",
];
const CURRENCIES = ["THB", "LAK", "USD"];

function toast(icon, text) {
  Swal.fire({ icon, text, timer: 2500, toast: true, position: "top-right", showConfirmButton: false });
}

// Staff correction actions for a single SlipBotEvent — shown at the bottom of
// the detail dialog. Reuses existing, already-tested money-moving endpoints
// (rejectTopupRequest / adminAdjustBalance) wherever possible; only the
// "create top-up from a missed slip" action is genuinely new (createManualTopup).
function ManualActions({ event, onChanged, t }) {
  const [topup, setTopup] = useState(null); // linked WalletTopupRequest, if any
  const [loadingTopup, setLoadingTopup] = useState(false);
  const [mode, setMode] = useState(null); // 'create' | 'reject' | null
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("THB");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null); // {id, name}
  const [adjustBalances, setAdjustBalances] = useState({});

  useEffect(() => {
    setMode(null); setAmount(""); setCurrency("THB"); setNote("");
    if (event.topupRequestId) {
      setLoadingTopup(true);
      getTopupRequestDetails(event.topupRequestId)
        .then((res) => setTopup(res && res.data ? res.data : null))
        .catch(() => setTopup(null))
        .finally(() => setLoadingTopup(false));
    } else {
      setTopup(null);
    }
  }, [event._id, event.topupRequestId]);

  if (event.manualReview && event.manualReview.action) {
    const mr = event.manualReview;
    return (
      <Box sx={{ mt: 2, p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
        <Typography variant="body2" color="secondary">
          {t("slipbotHistory.actions.reviewedBy", { name: (mr.reviewedBy && mr.reviewedBy.name) || "?" })} · {fmtDateTime(mr.reviewedAt)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`slipbotHistory.actions.action_${mr.action}`)}{mr.note ? ` — ${mr.note}` : ""}
        </Typography>
      </Box>
    );
  }

  const submitCreate = async () => {
    setBusy(true);
    const res = await createManualTopup(event._id, { amount, currency, note });
    setBusy(false);
    if (res && res.success) {
      toast("success", t("slipbotHistory.actions.createdSuccess"));
      onChanged();
    } else {
      toast("error", (res && res.message) || t("common.error"));
    }
  };

  const confirmCorrect = async () => {
    setBusy(true);
    const res = await reviewSlipBotEvent(event._id, { action: "confirmed_correct", note });
    setBusy(false);
    if (res && res.success) { toast("success", t("common.success")); onChanged(); }
    else toast("error", (res && res.message) || t("common.error"));
  };

  const submitReject = async () => {
    setBusy(true);
    const res = await rejectTopupRequest(event.topupRequestId, note);
    if (res && res.success) {
      await reviewSlipBotEvent(event._id, { action: "rejected_topup", note });
      setBusy(false);
      toast("success", t("common.success"));
      onChanged();
    } else {
      setBusy(false);
      toast("error", (res && res.message) || t("common.error"));
    }
  };

  const openReverse = async () => {
    setBusy(true);
    const acc = await getCustomerAccountByAdmin(event.customerId);
    setBusy(false);
    if (!acc || !acc.success) { toast("error", (acc && acc.message) || t("common.error")); return; }
    setAdjustTarget({ id: acc.data.id, name: (event.customer && event.customer.name) || event.from });
    setAdjustBalances({ THB: acc.data.THB, LAK: acc.data.LAK, USD: acc.data.USD });
    setAdjustOpen(true);
  };

  const onReverseSuccess = async () => {
    setAdjustOpen(false);
    await reviewSlipBotEvent(event._id, { action: "reversed_credit", note: t("slipbotHistory.actions.reversedNote") });
    toast("success", t("common.success"));
    onChanged();
  };

  const creatable = CREATABLE_OUTCOMES.includes(event.outcome);
  const stillPending = ["created", "created_flagged"].includes(event.outcome) && topup && topup.status === "pending";
  const wasApproved = event.outcome === "auto_approved" && topup && topup.status === "approved";

  if (loadingTopup) return <Box sx={{ mt: 2 }}><CircularProgress size={20} color="secondary" /></Box>;
  if (!creatable && !stillPending && !wasApproved) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography variant="subtitle2" color="secondary" sx={{ mb: 1 }}>{t("slipbotHistory.actions.title")}</Typography>

      {creatable && mode === null && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" color="secondary" size="small" onClick={() => setMode("create")}>
            {t("slipbotHistory.actions.createTopup")}
          </Button>
          <Button variant="outlined" color="secondary" size="small" onClick={confirmCorrect} disabled={busy}>
            {t("slipbotHistory.actions.confirmCorrect")}
          </Button>
        </Box>
      )}

      {mode === "create" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField size="small" type="number" label={t("slipbotHistory.actions.amount")} value={amount} onChange={(e) => setAmount(e.target.value)} sx={{ width: 140 }} />
          <FormControl size="small" sx={{ width: 100 }}>
            <InputLabel>{t("slipbotHistory.actions.currency")}</InputLabel>
            <Select value={currency} label={t("slipbotHistory.actions.currency")} onChange={(e) => setCurrency(e.target.value)}>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label={t("slipbotHistory.actions.note")} value={note} onChange={(e) => setNote(e.target.value)} sx={{ minWidth: 180, flex: 1 }} />
          <Button variant="contained" color="secondary" size="small" onClick={submitCreate} disabled={busy || !amount}>
            {busy ? <CircularProgress size={18} color="inherit" /> : t("slipbotHistory.actions.submit")}
          </Button>
          <Button variant="text" color="secondary" size="small" onClick={() => setMode(null)} disabled={busy}>
            {t("common.cancel")}
          </Button>
        </Box>
      )}

      {stillPending && mode === null && (
        <Button variant="outlined" color="error" size="small" onClick={() => setMode("reject")}>
          {t("slipbotHistory.actions.rejectTopup")}
        </Button>
      )}
      {mode === "reject" && (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "flex-start" }}>
          <TextField size="small" label={t("slipbotHistory.actions.reasonRequired")} value={note} onChange={(e) => setNote(e.target.value)} sx={{ minWidth: 220, flex: 1 }} />
          <Button variant="contained" color="error" size="small" onClick={submitReject} disabled={busy || !note.trim()}>
            {busy ? <CircularProgress size={18} color="inherit" /> : t("slipbotHistory.actions.submit")}
          </Button>
          <Button variant="text" color="secondary" size="small" onClick={() => setMode(null)} disabled={busy}>
            {t("common.cancel")}
          </Button>
        </Box>
      )}

      {wasApproved && (
        <Button variant="outlined" color="error" size="small" onClick={openReverse} disabled={busy}>
          {busy ? <CircularProgress size={18} color="inherit" /> : t("slipbotHistory.actions.reverseCredit")}
        </Button>
      )}
      {adjustTarget && (
        <AdjustBalanceDialog
          open={adjustOpen}
          onClose={() => setAdjustOpen(false)}
          onSuccess={onReverseSuccess}
          target={adjustTarget}
          balances={adjustBalances}
        />
      )}
    </Box>
  );
}

function DetailDialog({ event, onClose, onChanged, t }) {
  if (!event) return null;
  const ocr = event.ocr || {};
  const filename = event.mediaFile && event.mediaFile.filename;

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 400 }}>
          <Box sx={{ flex: "1 1 45%", bgcolor: "#111", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", minHeight: { xs: 260, md: "auto" } }}>
            <IconButton onClick={onClose} sx={{ position: "absolute", top: 8, right: 8, color: "#fff", bgcolor: "rgba(0,0,0,0.5)", zIndex: 1 }} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
            {filename ? (
              <Box component="img" src={slipBotMediaUrl(filename)} alt={filename} sx={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
            ) : (
              <Typography sx={{ color: "#888", p: 4 }}>{t("slipbotHistory.noImage")}</Typography>
            )}
          </Box>

          <Box sx={{ flex: "1 1 55%", p: 2.5, overflowY: "auto", maxHeight: { md: "80vh" } }}>
            <Typography variant="h6" color="secondary" sx={{ mb: 0.5 }}>
              {event.customer ? `${event.customer.name} (${event.customer.whatsapp})` : event.from}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {fmtDateTime(event.createdAt)}
              {ocr.durationMs != null ? ` · OCR ${(ocr.durationMs / 1000).toFixed(1)}s` : ""}
              {ocr.slipDatetime ? ` · ${t("slipbotHistory.slipDatetime")}: ${ocr.slipDatetime}` : ""}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
              <Chip size="small" color={OUTCOME_COLOR[event.outcome] || "default"} label={t(`slipbotHistory.outcomes.${event.outcome}`)} />
              <ClassifierChip ocr={ocr} t={t} />
              {event.amount != null && (
                <Chip size="small" variant="outlined" label={`${Number(event.amount).toLocaleString()} ${event.currency || ""}`} />
              )}
            </Box>

            {event.error && (
              <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
                {t("slipbotHistory.error")}: {event.error}
              </Typography>
            )}

            {(ocr.slipSignals || []).length > 0 && (
              <>
                <Typography variant="subtitle2" color="secondary">{t("slipbotHistory.signals")}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5, mt: 0.5 }}>
                  {ocr.slipSignals.map((s, i) => (
                    <Tooltip key={i} title={s.evidence || ""}>
                      <Chip size="small" variant="outlined" color="secondary" label={`${s.name} +${s.weight}`} />
                    </Tooltip>
                  ))}
                </Box>
              </>
            )}

            {(ocr.candidates || []).length > 0 && (
              <>
                <Typography variant="subtitle2" color="secondary">{t("slipbotHistory.candidates")}</Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.5, mt: 0.5 }}>
                  {ocr.candidates.map((c, i) => (
                    <Chip
                      key={i} size="small" variant="outlined"
                      label={`${c.raw_text}${c.currency_hint ? ` [${c.currency_hint}]` : ""} (${Number(c.confidence).toFixed(2)})`}
                    />
                  ))}
                </Box>
              </>
            )}

            {(event.expected || []).length > 0 && (
              <>
                <Typography variant="subtitle2" color="secondary">{t("slipbotHistory.expectedTargets")}</Typography>
                <Box sx={{ mb: 1.5, mt: 0.5, maxHeight: 140, overflowY: "auto" }}>
                  {event.expected.slice(0, 30).map((x, i) => (
                    <Typography key={i} variant="caption" component="div" color="text.secondary">
                      {Number(x.targetValue).toLocaleString()} {x.currency} — {x.basis} ({x.thbBase} THB)
                    </Typography>
                  ))}
                </Box>
              </>
            )}

            {(ocr.referenceNumbers || []).length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t("slipbotHistory.referenceNumbers")}: {ocr.referenceNumbers.join(", ")}
              </Typography>
            )}

            {ocr.fullText && (
              <>
                <Typography variant="subtitle2" color="secondary">{t("slipbotHistory.fullText")}</Typography>
                <Box sx={{ fontFamily: "ui-monospace, Consolas, monospace", fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word", bgcolor: "action.hover", borderRadius: 1, p: 1.5, mt: 0.5, maxHeight: 200, overflowY: "auto" }}>
                  {ocr.fullText}
                </Box>
              </>
            )}

            <ManualActions event={event} onChanged={onChanged} t={t} />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

const SlipBotHistory = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [outcome, setOutcome] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    const res = await getSlipBotEvents({ page: p, limit: 20, outcome, search, dateFrom, dateTo });
    setRows(res && res.success ? res.data : []);
    setPagination((res && res.pagination) || { page: 1, pages: 1, total: 0 });
    setLoading(false);
  }, [outcome, search, dateFrom, dateTo]);

  useEffect(() => { setPage(1); load(1); }, [outcome, search, dateFrom, dateTo, load]);

  // Debounce free-text search so we don't hit the API per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 500);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const handlePageChange = (e, p) => { setPage(p); load(p); };

  // After a manual-decision action succeeds: reload the current page so the
  // outcome/manualReview badge is fresh, and close the dialog — its `event`
  // prop is now stale (the row it came from has been refetched separately).
  const handleActionChanged = () => { setDetail(null); load(page); };

  return (
    <Box m="0 1rem 2rem">
      <Header title={t("slipbotHistory.title")} subtitle={t("slipbotHistory.subtitle")} />

      <Box sx={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", mb: 2, mt: 2 }}>
        <TextField
          size="small" label={t("slipbotHistory.search")} placeholder={t("slipbotHistory.searchPlaceholder")}
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 230 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>{t("slipbotHistory.outcome")}</InputLabel>
          <Select value={outcome} label={t("slipbotHistory.outcome")} onChange={(e) => setOutcome(e.target.value)}>
            <MenuItem value="">{t("slipbotHistory.all")}</MenuItem>
            {OUTCOMES.map((o) => (
              <MenuItem key={o} value={o}>{t(`slipbotHistory.outcomes.${o}`)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small" type="datetime-local" label={t("slipbotHistory.dateFrom")} sx={{ minWidth: 200 }}
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small" type="datetime-local" label={t("slipbotHistory.dateTo")} sx={{ minWidth: 200 }}
          value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined" color="secondary"
          onClick={() => { setSearchInput(""); setOutcome(""); setDateFrom(""); setDateTo(""); }}
        >
          {t("common.clear")}
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
          {pagination.total || 0} {t("slipbotHistory.results")}
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ p: 3 }}><CircularProgress color="secondary" /></Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ p: 2 }}>{t("slipbotHistory.noEvents")}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t("slipbotHistory.customer")}</TableCell>
                <TableCell>{t("slipbotHistory.outcome")}</TableCell>
                <TableCell>{t("slipbotHistory.classifier")}</TableCell>
                <TableCell align="right">{t("slipbotHistory.amount")}</TableCell>
                <TableCell align="right">{t("slipbotHistory.duration")}</TableCell>
                <TableCell>{t("slipbotHistory.time")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e._id} hover sx={{ cursor: "pointer" }} onClick={() => setDetail(e)}>
                  <TableCell sx={{ width: 68 }}>
                    <Thumb event={e} onClick={() => setDetail(e)} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{e.customer ? e.customer.name : e.from}</Typography>
                    {e.customer && (
                      <Typography variant="caption" color="text.secondary">{e.customer.whatsapp}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={OUTCOME_COLOR[e.outcome] || "default"} label={t(`slipbotHistory.outcomes.${e.outcome}`)} />
                    {e.error && (
                      <Tooltip title={e.error}>
                        <Typography variant="caption" color="error" component="div" sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {e.error}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell><ClassifierChip ocr={e.ocr} t={t} /></TableCell>
                  <TableCell align="right">
                    {e.amount != null ? `${Number(e.amount).toLocaleString()} ${e.currency || ""}` : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {e.ocr && e.ocr.durationMs != null ? `${(e.ocr.durationMs / 1000).toFixed(1)}s` : "—"}
                  </TableCell>
                  <TableCell>{fmtDateTime(e.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pagination.pages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <Pagination color="secondary" count={pagination.pages} page={page} onChange={handlePageChange} />
        </Box>
      )}

      <DetailDialog event={detail} onClose={() => setDetail(null)} onChanged={handleActionChanged} t={t} />
    </Box>
  );
};

export default SlipBotHistory;
